import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import {
  FORM_DATE_FIELD_CLASS,
  FORM_FIELD_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/DatePicker'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

export interface RepeatState {
  enabled: boolean
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly'
  weekdays: number[]
  end_date: string
  occurrences: string
}

// eslint-disable-next-line react-refresh/only-export-components
export const defaultRepeatState: RepeatState = {
  enabled: false,
  frequency: 'weekly',
  weekdays: [],
  end_date: '',
  occurrences: '',
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

interface Props {
  value: RepeatState
  onChange: (next: RepeatState) => void
}

// 2.3.15 Repeat schedule — toggle + frequency + day selector + end/occurrences.
export function RepeatScheduleSection({ value, onChange }: Props) {
  const { t } = useTranslation('jobs')
  const set = (patch: Partial<RepeatState>) => onChange({ ...value, ...patch })
  const showDays =
    value.frequency === 'weekly' || value.frequency === 'biweekly'

  const toggleDay = (idx: number) =>
    set({
      weekdays: value.weekdays.includes(idx)
        ? value.weekdays.filter(d => d !== idx)
        : [...value.weekdays, idx],
    })

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3 border-b border-[#e7e9ef] pb-4'>
        <Label className='text-sm font-medium leading-[1.4] text-[#171a20]'>
          {t('repeat.repeat')}
        </Label>
        <Switch
          checked={value.enabled}
          onCheckedChange={c => set({ enabled: c })}
        />
      </div>

      {value.enabled && (
        <div className='space-y-4'>
          <div className='space-y-1'>
            <Label className='text-xs font-medium leading-4'>
              {t('repeat.repeat')}
            </Label>
            <div className='flex flex-wrap gap-2'>
              {(['daily', 'weekly', 'biweekly', 'monthly'] as const).map(
                frequency => (
                  <button
                    key={frequency}
                    type='button'
                    onClick={() => set({ frequency })}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-normal leading-4 shadow-[0_1px_4px_rgba(38,45,66,0.05)]',
                      value.frequency === frequency
                        ? 'border-[#10b981] bg-[#ecfdf5] text-[#047857]'
                        : 'border-[#e7e9ef] bg-white text-[#171a20]'
                    )}
                  >
                    {value.frequency === frequency && (
                      <Check className='size-4 shrink-0 stroke-[2.2] text-[#10b981]' />
                    )}
                    {t(`repeat.frequencies.${frequency}`)}
                  </button>
                )
              )}
            </div>
          </div>

          {showDays && (
            <div className='space-y-2'>
              <Label className='text-xs font-medium leading-4'>
                {t('repeat.repeatOn')}
              </Label>
              <div className='flex flex-wrap gap-2'>
                {DAY_KEYS.map((d, i) => {
                  const checked = value.weekdays.includes(i)
                  return (
                    <label
                      key={d}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-normal leading-4 shadow-[0_1px_4px_rgba(38,45,66,0.05)]',
                        checked
                          ? 'border-[#10b981] bg-[#ecfdf5] text-[#047857]'
                          : 'border-[#e7e9ef] bg-white text-[#171a20]'
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleDay(i)}
                        className='border-[#d0d5dd] data-[state=checked]:border-[#10b981] data-[state=checked]:bg-[#10b981] data-[state=checked]:text-white'
                      />
                      {t(`repeat.days.${d}`)}
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
            <div className='min-w-0 flex-1 space-y-1'>
              <Label
                htmlFor='repeat_end'
                className='text-xs font-medium leading-4'
              >
                {t('repeat.until')}
              </Label>
              <DatePicker
                id='repeat_end'
                value={value.end_date}
                onChange={next => set({ end_date: next })}
                variant='field'
                className={FORM_DATE_FIELD_CLASS}
              />
            </div>
            <span className='flex h-10 shrink-0 items-center text-sm font-normal leading-[1.4] text-[#6c7285]'>
              {t('repeat.orAfter')}
            </span>
            <div className='flex min-w-0 flex-1 items-center gap-3'>
              <Input
                id='repeat_occ'
                type='number'
                min={1}
                value={value.occurrences}
                onChange={e => set({ occurrences: e.target.value })}
                placeholder={t('repeat.number')}
                aria-label={t('repeat.occurrences')}
                className={FORM_FIELD_CLASS}
              />
              <span className='shrink-0 text-sm font-normal leading-[1.4] text-[#6c7285]'>
                {t('repeat.visits')}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
